import React, {
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";

import Autocomplete from "@mui/material/Autocomplete";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import { usePrincipal } from "authentication/AuthProvider";
import OrganisationUserClient from "organisation/user/OrganisationUserClient";
import useFetch from "utils/fetch/useFetch";

export type UserAccessPlaceholderRowProps = {
	users: string[];

	onInsert: (user: string) => void;
};

export default function UserAccessPlaceholderRow(
	props: Readonly<UserAccessPlaceholderRowProps>
) {
	const principal = usePrincipal();
	const { call } = useFetch();

	const [organisationUsers, setOrganisationUsers] = useState<string[]>([]);
	const availableUsers = useMemo(
		() => organisationUsers.filter(user => !props.users.includes(user)),
		[props.users, organisationUsers]
	);

	useEffect(() => {
		if (
			!principal ||
			!principal.isPrivateOrganisation() ||
			!principal.organisationId
		) {
			setOrganisationUsers([]);
			return;
		}

		call(OrganisationUserClient.getAll(principal.organisationId)).then(
			response => setOrganisationUsers(response.map(user => user.email))
		);
	}, [principal]);

	const insertValue = useCallback(
		(value: string): void => {
			const cleanValue = value.trim();
			if (cleanValue.length > 0) {
				props.onInsert(cleanValue);
			}
		},
		[props.onInsert]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>): void => {
			if (e.key === "Enter") {
				const element = e.target as HTMLInputElement;
				insertValue(element.value);
			}
		},
		[insertValue]
	);

	const handleBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>): void => {
			insertValue(e.target.value);
		},
		[insertValue]
	);

	const handleSelect = useCallback(
		(_: SyntheticEvent, value: string | null): void => {
			if (value !== null) {
				insertValue(value);
			}
		},
		[insertValue]
	);

	return (
		<TableRow>
			<TableCell>
				{availableUsers.length > 0 ? (
					<Autocomplete
						clearOnBlur={false}
						selectOnFocus={false}
						options={availableUsers}
						onChange={handleSelect}
						renderInput={params => (
							<TextField
								{...params}
								fullWidth
								onKeyDown={handleKeyDown}
								onBlur={handleBlur}
								variant="standard"
							/>
						)}
					/>
				) : (
					<TextField
						fullWidth
						onKeyDown={handleKeyDown}
						onBlur={handleBlur}
						variant="standard"
					/>
				)}
			</TableCell>
			<TableCell />
		</TableRow>
	);
}
